// The verification form.
//
// Ported from app/views/scholarship/verification.haml, which the rebuild had reduced to a
// checkbox. It is not a checkbox: it is a printed form a guidance counselor and a parent
// fill in and sign, and the page also carries the list of documents to post and the rules
// about how. Those rules carry disqualification as a consequence, so leaving them out is not
// a cosmetic omission.
//
// The print stylesheet hides the surrounding chrome so the printed page is the form alone.

import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.ts';
import { Notice, ErrorNotice, Loading, Checkbox } from '../components/Bits.tsx';
import { useApplication } from './Dashboard.tsx';

/** A ruled line to write on. */
function Rule({ label }: { label: string }) {
  return (
    <div style={{ marginBottom: '1.4rem' }}>
      <div style={{ borderBottom: '1px solid #000', height: '1.6rem' }} />
      <div style={{ fontSize: '0.8rem', marginTop: '0.2rem' }}>{label}</div>
    </div>
  );
}

export function Verification() {
  const { data, isPending } = useApplication();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const confirm = useMutation({
    mutationFn: (confirmed: boolean) => api.put('/api/application/verification', { confirmed }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['application'] }),
  });

  if (isPending) return <main id="main"><Loading /></main>;

  const done = data?.applicant.verificationForm ?? false;
  const year = data?.cycle?.year ?? new Date().getUTCFullYear() + 1;

  return (
    <main id="main">
      <div className="screen-only">
        <p><Link to="/apply">← Back to your application</Link></p>
        <h2>Verification form</h2>
        <p className="lede">
          Print this page. Your guidance counselor and a parent complete and sign it, and it is
          posted to the foundation with your other documents. There is nothing to fill in online.
        </p>

        <ErrorNotice error={confirm.error} />

        <div className="actions" style={{ marginBottom: '1.5rem' }}>
          <button onClick={() => window.print()}>Print this form</button>
        </div>
      </div>

      {/* Everything below prints. */}
      <div className="printable">
        <h3 style={{ marginTop: 0 }}>SEED Foundation Scholarship — {year} Verification Form</h3>

        <div className="row">
          <Rule label="Applicant's name" />
          <Rule label="Email address" />
        </div>

        <p style={{ fontStyle: 'italic', marginBottom: '0.8rem' }}>
          To be completed by the high school guidance counselor:
        </p>
        <div className="row">
          <Rule label="G.P.A. (scale of 4.0)" />
          <Rule label="SAT / ACT score" />
        </div>
        <div className="row">
          <Rule label="Name of guidance counselor (print)" />
          <Rule label="Guidance counselor's signature" />
        </div>

        <p style={{ fontStyle: 'italic', marginTop: '1.2rem', marginBottom: '0.8rem' }}>
          To be completed by a parent:
        </p>
        <div className="row">
          <Rule label={`Adjusted Gross Income from the ${year - 2} IRS 1040, line 11`} />
          <Rule label="Number of siblings in undergraduate studies" />
        </div>

        <p style={{ fontSize: '0.9rem' }}>
          I certify that the statements on this form and on the application submitted via the
          internet are true, complete and accurate. I understand this information will be
          treated as confidential for review. I agree to the use of my name and the information
          in this application package for SEED Foundation Scholarship purposes without further
          compensation or notification.
        </p>
        <p style={{ fontSize: '0.9rem', fontWeight: 600 }}>
          By signing below, I certify that I am a legal resident of the United States of America.
        </p>

        <div className="row" style={{ marginTop: '1.5rem' }}>
          <Rule label="Parent's signature" />
          <Rule label="Applicant's signature" />
        </div>

        <h4 style={{ marginTop: '2rem' }}>Documents to post to the SEED Foundation</h4>
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {[
            'Official transcript — the official seal must be present on the envelope. Please highlight the GPA and SAT/ACT scores.',
            'Letter of recommendation, sealed — submit ONE only.',
            'Completed verification form (this document).',
          ].map((item) => (
            <li key={item} style={{ marginBottom: '0.5rem' }}>
              <span style={{ border: '1px solid #000', display: 'inline-block', width: '0.9rem', height: '0.9rem', marginRight: '0.6rem' }} />
              {item}
            </li>
          ))}
        </ul>

        <p style={{ fontSize: '0.9rem' }}>
          <strong><u>Important.</u></strong>{' '}
          <em>
            Please send nothing beyond what is requested — additional material disqualifies the
            application automatically. Send <strong>all</strong> of the above in{' '}
            <strong>one package</strong>; do not post anything separately. Do <strong>not</strong>{' '}
            use certified mail or any service requiring a signature, as we cannot accept
            signature-required mail and doing so will disqualify the application.
          </em>
        </p>

        <p>
          Postmark everything by <strong>April 30th, {year}</strong> and post to:
        </p>
        <p style={{ marginLeft: '1rem' }}>
          Nupur Gupta<br />
          SEED Foundation Scholarship Program<br />
          2500 Independence Ave<br />
          Glenview, IL 60026
        </p>
      </div>

      <div className="screen-only">
        {done && (
          <Notice kind="ok">You have confirmed the signed form has been posted.</Notice>
        )}

        <div className="panel">
          <Checkbox
            checked={done}
            onChange={(e) => confirm.mutate(e.target.checked)}
            label={<>
              The verification form has been signed by my guidance counselor and a parent, and
              posted to the SEED Foundation with my transcript and recommendation.
            </>}
          />
          <p className="hint" style={{ marginTop: '0.5rem' }}>
            Your application is not reviewed until the signed form reaches us.
          </p>
          <div className="actions">
            <button className="secondary" onClick={() => navigate('/apply')}>
              Back to your application
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
